import React, { useMemo } from "react";
import * as tokens from "@amzn/awsui-design-tokens-core";
import {
  Box,
  ExpandableSection,
  LineChart,
  MixedLineBarChartProps,
} from "@amzn/awsui-components-react";
import {
  Investigation,
  InvestigationTrendMarker,
  InvestigationTrendPoint,
} from "../../lusca-search-api/generated-src";
import { TrendSparkline } from "./TrendSparkline";
import { cleanTrend, hasEstimates, pointTime, pointValue } from "./trendUtils";

export const LIVE_SERIES_TITLE = "Matching this rule in the DLQ";
export const OWNED_SERIES_TITLE = "Owned by this investigation";

const formatTime = (d: Date) =>
  d.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });

export function trendSeries(
  trend?: InvestigationTrendPoint[] | null
): MixedLineBarChartProps.LineDataSeries<Date>[] {
  const points = cleanTrend(trend);
  if (!points.length) return [];
  const at = (p: InvestigationTrendPoint) => new Date(pointTime(p));
  const series: MixedLineBarChartProps.LineDataSeries<Date>[] = [
    {
      title: LIVE_SERIES_TITLE,
      type: "line",
      data: points.map((p) => ({ x: at(p), y: pointValue(p) })),
    },
  ];
  // Owned differs from matches once a child split or another case takes some of the items.
  if (points.some((p) => typeof p.live === "number" && p.live !== p.n)) {
    series.push({
      title: OWNED_SERIES_TITLE,
      type: "line",
      data: points.map((p) => ({ x: at(p), y: p.n })),
    });
  }
  return series;
}

const MARKER_COLORS: Record<string, string> = {
  split: tokens.colorChartsThresholdNeutral,
  merge: tokens.colorChartsThresholdNeutral,
  redrive: tokens.colorChartsThresholdInfo,
  fix: tokens.colorChartsStatusMedium,
  resolved: tokens.colorChartsThresholdPositive,
};

/** Line series as the chart consumes them; markers are x-thresholds (vertical lines). */
type TrendChartSeries = MixedLineBarChartProps.LineDataSeries<Date>;

/**
 * Vertical marker lines for events that explain a bend (split, merge, redrive, fix, resolved).
 * Only markers inside the charted time range are drawn, so an old event does not stretch the axis.
 * Cloudscape draws `{type: "threshold", x}` as a vertical line; the public LineChart typings only
 * list horizontal thresholds, hence the cast at the call site.
 */
export function markerSeries(
  markers: InvestigationTrendMarker[] | undefined | null,
  trend: InvestigationTrendPoint[] | undefined | null,
  now: number = Date.now()
): MixedLineBarChartProps.XThresholdSeries<Date>[] {
  const points = cleanTrend(trend);
  if (!points.length) return [];
  const from = pointTime(points[0]);
  return (markers ?? [])
    .map((m) => ({ m, at: new Date(m.t).getTime() }))
    .filter(({ at }) => !Number.isNaN(at) && at >= from && at <= now)
    .sort((a, b) => a.at - b.at)
    .map(({ m, at }) => ({
      type: "threshold" as const,
      x: new Date(at),
      title: `${m.label} (${formatTime(new Date(at))})`,
      color: MARKER_COLORS[m.kind] ?? tokens.colorChartsThresholdNeutral,
    }));
}

/**
 * The investigation's population over time, on the Population tab: one point per reconciled DLQ
 * snapshot (every snapshot of the last 48 hours, then one per day). The main line is everything
 * the rule matches in the DLQ, so a split or merge does not look like the issue shrinking; the
 * owned line appears once the two differ.
 */
export const InvestigationTrendChart: React.FC<{
  investigation: Investigation;
}> = ({ investigation }) => {
  const { trend, trendMarkers } = investigation;
  const series = useMemo(
    () => [
      ...trendSeries(trend),
      // Vertical lines are supported at runtime but missing from LineChart's typings.
      ...(markerSeries(trendMarkers, trend) as unknown as TrendChartSeries[]),
    ],
    [trend, trendMarkers]
  );
  const points = cleanTrend(trend);
  if (!points.length) return null;
  return (
    <ExpandableSection
      variant="container"
      defaultExpanded
      headerText={
        <span>
          Population over time <TrendSparkline trend={trend} />
        </span>
      }
      headerDescription="Items matching the rule per DLQ snapshot. Red means growing over the last 7 days; items moved to a split or another case still count. Vertical lines mark splits, merges, redrives and status changes."
    >
      <LineChart<Date>
        series={series}
        xScaleType="time"
        height={180}
        hideFilter
        ariaLabel="Investigation population per DLQ snapshot"
        xTitle="Snapshot"
        yTitle="Items"
        xTickFormatter={formatTime}
        i18nStrings={{
          xTickFormatter: formatTime,
          detailPopoverDismissAriaLabel: "Dismiss",
          legendAriaLabel: "Legend",
          chartAriaRoleDescription: "line chart",
        }}
        detailPopoverSeriesContent={({ series: s, y }) => ({
          key: s.title,
          // A marker (vertical threshold) has no value of its own.
          value: typeof y === "number" ? y.toLocaleString() : "",
        })}
        empty={
          <Box textAlign="center" color="inherit">
            No snapshots recorded yet.
          </Box>
        }
      />
      {points.length === 1 && (
        <Box color="text-body-secondary" fontSize="body-s">
          One snapshot so far; the trend appears after the next DLQ snapshot.
        </Box>
      )}
      {hasEstimates(trend) && (
        <Box color="text-body-secondary" fontSize="body-s">
          Some points are estimates: the rule matched more items than
          reconciliation assigns exactly, so they show the rule&apos;s match
          count.
        </Box>
      )}
    </ExpandableSection>
  );
};

export default InvestigationTrendChart;
